import { useContext } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import {
  AppHeader,
  AppHeaderItem,
  AppHeaderUserMenu,
  AppHeaderWorkspace
} from "@redelair-sa/intiza-ui";
import { TranslationService } from "../../../services/TranslationService";
import CompanyService from "../../../services/CompanyService";
import SocketContext from "../SocketContext";
import ModalService from "../bootstrap/modal/Modal";
import { Message } from "../SupportButton";
import FloatingPanelService from "../FloatingPanel";
import { NewCompany } from "../NewCompany";
import { getLinkTo } from "../utils/LinkUtils";
import intizaLogoPng from "../../../assets/img/intizaLogoWhite.png";
import { AIAgentType } from "../../../entities/company/AIBilling/AIBilling";
import AssistantButton from "./AssistantButton";
import SwitchButton from "./SwitchButton";
import AnnouncementsBell from "./announcements/AnnouncementsBell";
import { isFeatureEnabled } from "../../../utils/featureFlags";
import { useViewAllSegments } from "../../../hooks/useViewAllSegments";

const CONSOLIDATED_ITEM_ID = "consolidated";

function obtenerIniciales() {
  const userName = CompanyService.getUsers().find(
    (x) => x.Id === CompanyService.getUserid()
  )?.Value;
  if (!userName) {
    return "-";
  }
  const [nombre, apellido] = userName.split(" ");
  if (nombre && apellido) {
    return `${nombre.charAt(0).toUpperCase()}${apellido.charAt(0).toUpperCase()}`;
  }
  return "-";
}

const AppHeaderContainer: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { online, setOnline, setOffline } = useContext(SocketContext);
  // Preloaded on mount so the menu opens with the toggle already resolved
  const viewAllSegments = useViewAllSegments();

  const isConsolidatedView =
    CompanyService.canViewConsolidated() &&
    (location.pathname.includes("/consolidated") ||
      new URLSearchParams(location.search).get("consolidated") === "1");

  // El indicador de disponibilidad solo aplica a usuarios que realmente
  // pueden recibir notificaciones de WhatsApp (GDC-5163).
  const showWhatsappStatus = CompanyService.canUseWhatsappNotifications();

  const openTechnicalSupportModal = () => {
    ModalService.showDefaultModal({
      title: TranslationService.translate.ContactSupport,
      hideClose: true,
      size: "modal-lg",
      message: Message
    });
  };

  // Asistente IA (GDC-2950/2951): solo si la empresa tiene habilitado el agente
  // de chat. En la vista consolidada no aplica (opera sobre una empresa puntual).
  const showAssistant =
    !isConsolidatedView && CompanyService.canShowAgentAI(AIAgentType.Chat);

  const companies: AppHeaderItem[] = CompanyService.getCompanies().map((c) => ({
    id: c.Id,
    name: c.Value
  }));

  // Solapa Cashapp: solo si la empresa tiene el modulo habilitado y el usuario el
  // switch prendido (el backend lo combina en el claim `cashapp`).
  const showCashapp = CompanyService.canShowCashApp();
  const workspaces: AppHeaderWorkspace[] = [
    { id: "cobranzas", label: "Cobranzas" },
    ...(showCashapp ? [{ id: "cashapp", label: "Conciliaciones" }] : [])
  ];

  // Navegacion a Cashapp: hand-off de sesion por querystring con el refreshToken
  // (cashapp-front lo recibe en su Authorization/CompanyLogin). Mismo mecanismo que AuthSite.
  const handleWorkspaceChange = (workspaceId: string) => {
    if (workspaceId !== "cashapp") {
      return;
    }
    const baseUrl = process.env.REACT_APP_CASHAPP_BASE_URL;
    const refreshToken = CompanyService.getCompanyAuth()?.refreshToken;
    if (!baseUrl || !refreshToken) {
      return;
    }
    const lang = TranslationService.currentLanguage;
    window.location.href = `${baseUrl.replace(/\/$/, "")}/${lang}?token=${refreshToken}`;
  };

  const topItems: AppHeaderItem[] = [
    { id: "processlog", name: TranslationService.translate.ProcessLog }
  ];

  const bottomItems: AppHeaderItem[] = [
    ...(CompanyService.canDo("newcompany")
      ? [{ id: "newcompany", name: TranslationService.translate.AddCompany }]
      : []),
    ...(CompanyService.canViewConsolidated()
      ? [
          {
            id: CONSOLIDATED_ITEM_ID,
            name: TranslationService.translate.Consolidated
          }
        ]
      : [])
  ];

  const handleTopItemClick = (item: AppHeaderItem) => {
    if (item.id === "processlog") {
      history.push(getLinkTo("processlog"));
    }
  };
  const handleBottomItemClick = (item: AppHeaderItem) => {
    if (item.id === "newcompany") {
      FloatingPanelService.showPanel({
        children: <NewCompany />,
        title: TranslationService.translate.NewCompany,
        height: 300,
        width: 600,
        position: "center"
      });
    } else if (item.id === CONSOLIDATED_ITEM_ID) {
      history.push(getLinkTo("consolidated"));
    }
  };

  const handleCompanyChange = (item: AppHeaderItem) => {
    history.push(getLinkTo(`switchcompany?to=${item.id}`));
  };

  // En la vista consolidada el selector debe mostrar "Consolidado" y no el
  // nombre de la ultima empresa seleccionada (comportamiento previo al nuevo menu).
  const activeCompanyId = isConsolidatedView
    ? CONSOLIDATED_ITEM_ID
    : CompanyService.getCompanyId();

  const user = {
    initials: obtenerIniciales(),
    ...(showWhatsappStatus
      ? {
          status: (online ? "online" : "offline") as "online" | "offline",
          onlineStatusLabel: TranslationService.translate.WhatsAppStatusOnline,
          offlineStatusLabel: TranslationService.translate.WhatsAppStatusOffline
        }
      : {})
  };

  const settingsHref = CompanyService.canEditCompanySettings()
    ? getLinkTo(isConsolidatedView ? "consolidated/settings" : "edit")
    : undefined;

  const accountHref = getLinkTo("account/edit");
  const userMenu: AppHeaderUserMenu = {
    name: CompanyService.getUserFullName(),
    email: CompanyService.getUserEmail(),
    account: {
      label: TranslationService.translate.MyAccount,
      href: accountHref,
      onClick: (e) => {
        e.preventDefault();
        history.push(accountHref);
      }
    },
    content: viewAllSegments.visible ? (
      <div className="d-flex align-items-center justify-content-between gap-2">
        <span>{TranslationService.translate.ViewAllSegments}</span>
        <SwitchButton
          label=""
          onChange={viewAllSegments.toggle}
          defaultValue={viewAllSegments.value}
          // The global .form-check rule (mockupStyles.css) forces min-width: 120px
          style={{ minWidth: 0, margin: 0 }}
        />
      </div>
    ) : undefined,
    logout: {
      label: TranslationService.translate.Logout,
      onClick: () => history.push(getLinkTo("logout"))
    }
  };

  return (
    <AppHeader
      // En la vista consolidada no corresponde mostrar el logo de una empresa
      // puntual; se oculta hasta que se vuelva a seleccionar una empresa.
      logo={
        isConsolidatedView ? undefined : (
          <Link to={"/"}>
            <img
              src={CompanyService.getCompanyAuth()?.logoUrl ?? intizaLogoPng}
              alt="Logo de Intiza"
              className="imgLogoControlWidth"
            />
          </Link>
        )
      }
      actions={showAssistant ? <AssistantButton /> : undefined}
      // TODO(GDC-5174): quitar la condición al integrar el backend de comunicados.
      notifications={
        isFeatureEnabled("announcements") ? <AnnouncementsBell /> : undefined
      }
      workspaces={workspaces}
      activeWorkspaceId="cobranzas"
      onWorkspaceChange={handleWorkspaceChange}
      companies={companies}
      activeCompanyId={activeCompanyId}
      onCompanyChange={handleCompanyChange}
      topItems={topItems}
      onTopItemClick={handleTopItemClick}
      bottomItems={bottomItems}
      onBottomItemClick={handleBottomItemClick}
      user={user}
      tooltips={{
        help: TranslationService.translate.ContactSupport,
        settings: TranslationService.translate.CompanySettings,
        user: TranslationService.translate.MyAccount
      }}
      onHelpClick={openTechnicalSupportModal}
      settingsHref={settingsHref}
      onSettingsClick={() => {
        if (settingsHref) {
          history.push(settingsHref);
        }
      }}
      userMenu={userMenu}
      {...(showWhatsappStatus
        ? { onUserStatusClick: () => (online ? setOffline() : setOnline()) }
        : {})}
    />
  );
};

export default AppHeaderContainer;
